/**
 * useProfileCompletionGate
 * ------------------------
 * Post-login "complete your profile" gate (Moodle-style forced completion,
 * but soft — the user can dismiss the modal; the check re-runs next login).
 *
 * Runs ONCE per browser session per user: fetches the self-service profile
 * form and, when any REQUIRED non-locked custom field is still empty,
 * redirects to /user/profile?completeProfile=1 — the profile header picks
 * up the query param, auto-opens the Edit Profile modal and shows a notice
 * listing the missing fields.
 *
 * Guard rails:
 *  - only runs when authenticated (mounted in MainLayout, which renders
 *    inside PrivateRoute — never on guest/login routes);
 *  - the sessionStorage flag is set BEFORE the API call, so it can never
 *    loop even if navigation or the fetch misbehaves;
 *  - fails open silently on API/storage errors;
 *  - locked-empty fields never count (the user can't fill them, and hidden
 *    fields are already excluded server-side from /users/me/profile-form);
 *  - skipped while impersonating (an admin acting as the user shouldn't be
 *    pushed into editing that user's profile).
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/shared/AuthContext';
import { UserApi, isEmptyProfileFieldValue } from '@/services/user.service';

const SESSION_FLAG = 'profile_completion_checked';

export function useProfileCompletionGate(): void {
    const navigate = useNavigate();
    const { isAuthenticated, user, isImpersonating } = useAuth();
    const userId = user?.id;

    useEffect(() => {
        if (!isAuthenticated || !userId) return;
        if (isImpersonating) return;

        // Once per session per user — storing the user id means logging in as
        // a DIFFERENT user in the same tab re-runs the check.
        try {
            if (sessionStorage.getItem(SESSION_FLAG) === String(userId)) return;
            sessionStorage.setItem(SESSION_FLAG, String(userId));
        } catch {
            return; // storage unavailable — fail open rather than risk a loop
        }

        let cancelled = false;
        UserApi.Profile.getMyProfileForm()
            .then((form) => {
                if (cancelled) return;
                const incomplete = (form.custom_fields || []).some(
                    (cf) => cf.required && !cf.locked && isEmptyProfileFieldValue(cf.datatype, cf.value),
                );
                if (incomplete) navigate('/user/profile?completeProfile=1');
            })
            .catch(() => {
                /* fail open — never block the app on a profile check */
            });
        return () => { cancelled = true; };
    }, [isAuthenticated, userId, isImpersonating, navigate]);
}
